import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../core/components/Button.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import { faFileImport } from '@fortawesome/pro-regular-svg-icons';
import classnames from '../../../core/utils/classnames.tsx';

const InsertIcon = withIcon(faFileImport);

export interface ISuggestion {
    // this represents a lookup entity
    articleId1: string;
    articleId2?: string;
    description: string;
    orderUnits: string[];
    salesUnits?: string[];
    quantity: number;
    unit: string;
    confidence?: number;
    // Trox customization
    orderCode?: any;
}

const ARTICLE_NUMBER_FOUND_THRESHOLD = 1.0;

const Suggestions = ({
    className,
    suggestions = [],
    onSelect,
    selectedArticleIds,
}: {
    className?: string;
    suggestions?: ISuggestion[];
    onSelect?: (item: any) => void;
    selectedArticleIds?: Set<string>;
}) => {
    const { t } = useTranslation('assistance');

    if (!suggestions.length) return null;

    // displayName is set to show custom trox fields
    return (
        <div className={classnames('flex flex-col gap-3 bg-surface-strong rounded-md p-3', className)}>
            <h4 className="text-content-medium text-sm">{t('listOfServices.suggestions.title')}</h4>
            {suggestions.map((item) => {
                const isArticleNumberFound =
                    item.confidence !== undefined && item.confidence >= ARTICLE_NUMBER_FOUND_THRESHOLD;

                let displayName = `${item.articleId1} ${item.articleId2 ? `, ${item.articleId2}` : ''}`;
                if (item.orderCode?.codeString) displayName = item.orderCode.codeString;
                return (
                    <div
                        key={item.articleId1}
                        className={classnames(
                            'flex gap-1 bg-surface-weak rounded px-3 py-2.5 shadow-sm transition-opacity hover:opacity-100'
                        )}
                    >
                        <div className="flex gap-4 items-center justify-between w-full flex-1">
                            <div className="flex flex-col flex-1 text-sm gap-0.5">
                                <h5 className="font-semibold">{displayName}</h5>

                                <p>{item.description}</p>
                            </div>

                            {isArticleNumberFound && (
                                <span className="flex bg-surface-success-weak text-xs text-content-success-medium border border-line-success-strong px-1.5 py-1 rounded-md font-medium">
                                    {t('listOfServices.suggestions.articleNumberFound')}
                                </span>
                            )}
                            {selectedArticleIds?.has(item.articleId1) && (
                                <span className="flex bg-surface-brand-weak text-xs text-content-brand-medium border border-line-brand-strong px-1.5 py-1 rounded-md font-medium">
                                    {t('listOfServices.suggestions.usedInDocument')}
                                </span>
                            )}
                        </div>

                        <Button className="flex-none" onClick={() => onSelect?.(item)} variant="ghost">
                            <InsertIcon />
                        </Button>
                    </div>
                );
            })}
        </div>
    );
};

export default Suggestions;
